/* eslint-disable no-loop-func */
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import classNames from "classnames";
import { Card, Col, Row, Spinner, InputGroup, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faUser,
  faPlus,
  faClock,
  faMessage,
  faBolt,
  faLightbulb,
  faSearch,
  faCircle,
  faBullhorn,
  faChartLine,
  faUsers,
  faUserPlus,
  faImage,
  faPaperclip,
  faDownload,
  faCheck,
  faCheckDouble,
  faTimes,
  faRobot
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Button from "components/base/Button";
import Backdrop from "components/common/Backdrop";
import axiosInstance from "helpers/api";
import { useAtom } from "jotai";
import { user_info } from "jotai/UserJotaiInstance";
import toast from "react-hot-toast";
import { formatErrorForReact } from 'helpers/utils';
import { UserInfo } from "interfaces/user";
import AddTeamMemberModal from "components/chat/AddTeamMemberModal";
import { useSocket } from "hooks/useSocket";

interface Attachment {
  type: 'file' | 'image';
  originalName?: string;  // Display name
  filename?: string;  // Alternative name field from backend
  savedFileName?: string;  // Legacy: local filesystem (deprecated)
  path?: string;  // Legacy: local path (deprecated)
  s3Key?: string;  // S3 storage key
  s3Location?: string;  // Full S3 URL (optional)
  url?: string;  // Direct S3 URL (preferred method)
  size: number;
  mimetype: string;
  bucket?: string;  // S3 bucket name
}

interface Message {
  id: number;
  text: string;
  sender: "user" | "assistant" | "collaborator";
  timestamp?: Date;
  type?: "text" | "insight" | "analysis";
  senderName?: string;
  lastUpdated?: number; // For forcing React re-renders during streaming
  attachments?: Attachment[];
  status?: "sending" | "sent" | "delivered" | "read";
}

interface AgentType {
  id: string;
  name: string;
  description: string;
  icon: any;
  color: string;
  type: 'ai' | 'human';
  systemPrompt?: string;
  userId?: string;
  gradient: string;
}

interface Collaborator {
  collaborator_id: string;
  invitee: {
    collaborator_first_name: string;
    collaborator_last_name: string;
    collaborator_email: string;
    collaborator_phone: string;
  };
  created_on: string;
  role?: string;
  status?: 'online' | 'away' | 'offline' | 'pending' | 'active';
  isPending?: boolean;
  isAuthenticated?: boolean;
  invitationSent?: boolean;
  // Conversation metadata
  conversationId?: string;
  lastMessage?: string;
  lastMessageTime?: Date;
  unreadCount?: number;
}

interface EnhancedConversation {
  id: string | number;
  name: string;
  lastMessage: string;
  timestamp: Date;
  unread?: boolean;
  agentType: string;
  participantId?: string;
  participantName?: string;
  systemPrompt?: string;
}

// AI Agent Definitions
const aiAgents: AgentType[] = [
  {
    id: 'broker-ai',
    name: 'Broker Assistant',
    description: 'Access broker resources, policies, and team documents',
    icon: faUsers,
    color: 'warning',
    type: 'ai',
    systemPrompt: 'You are the Broker Assistant for the KairosIQ Interoffice Communications Hub. You have access to broker-specific resources, policies, and procedures. When users ask questions, search through available documents to provide accurate, broker-specific answers. Always cite the source document when providing information.',
    gradient: 'linear-gradient(135deg, #ffc107, #ff6b6b)'
  },
  {
    id: 'document-ai',
    name: 'Document Assistant',
    description: 'Search and analyze uploaded documents and contracts',
    icon: faSearch,
    color: 'secondary',
    type: 'ai',
    systemPrompt: 'You are the Document Assistant for the KairosIQ Interoffice Communications Hub. You specialize in searching and analyzing uploaded documents, summarizing key points, and finding specific information. When answering questions, always reference the specific documents you are using.',
    gradient: 'linear-gradient(135deg, #6c757d, #495057)'
  },
  {
    id: 'marketing-ai',
    name: 'Marketing Agent',
    description: 'Marketing strategies and campaign optimization for real estate',
    icon: faBullhorn,
    color: 'primary',
    type: 'ai',
    systemPrompt: 'You are a friendly marketing coach for real estate agents. Keep responses conversational and under 3-4 sentences. Ask follow-up questions to understand their specific situation. Focus on one key insight at a time rather than overwhelming them with information. Be encouraging and practical.',
    gradient: 'linear-gradient(135deg, #0d6efd, #6610f2)'
  },
  {
    id: 'lead-analysis-ai',
    name: 'Lead Analysis Agent',
    description: 'Lead scoring, analysis, and task recommendations',
    icon: faChartLine,
    color: 'success',
    type: 'ai',
    systemPrompt: 'You are a supportive lead analysis coach for real estate agents. Give brief, actionable insights in 2-3 sentences. Always ask what specific challenge they want to tackle next. Focus on one clear next step rather than long lists. Be conversational and encouraging.',
    gradient: 'linear-gradient(135deg, #198754, #20c997)'
  },
  {
    id: 'general-ai',
    name: 'General Assistant',
    description: 'General CRM optimization and workflow assistance',
    icon: faBolt,
    color: 'info',
    type: 'ai',
    systemPrompt: 'You are a helpful business coach for real estate professionals. Keep responses short and conversational - aim for 2-4 sentences max. Ask questions to understand their specific needs. Provide one focused tip at a time. Be supportive and encouraging like a good coach.',
    gradient: 'linear-gradient(135deg, #0dcaf0, #6f42c1)'
  }
];

const dummyConversationHistory: EnhancedConversation[] = [
  {
    id: 1,
    name: "Marketing Strategy Discussion",
    lastMessage: "What's the best pricing strategy for my new listing?",
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    unread: false,
    agentType: "marketing-ai"
  },
  {
    id: 2,
    name: "Lead Analysis Session",
    lastMessage: "Analyze my recent lead quality trends",
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
    unread: true,
    agentType: "lead-analysis-ai"
  }
];

const Chat = ({ className }: any) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "👋 Hello! Welcome to **KAIROS IQ AI Assistant**. I'm here to help you with lead analysis, market insights, and CRM optimization. What would you like to explore today?",
      sender: "assistant",
      timestamp: new Date(),
      type: "text"
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | any>("");
  const [conversationHistory, setConversationHistory] = useState<EnhancedConversation[]>(dummyConversationHistory);
  const [loadingChatHistory, setLoadingChatHistory] = useState(false);
  const [activeConversation, setActiveConversation] = useState<EnhancedConversation | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showInlineSelection, setShowInlineSelection] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [inlineSearchQuery, setInlineSearchQuery] = useState("");
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [brokerageInfo, setBrokerageInfo] = useState<{ id: string; name: string; userRole: string } | null>(null);
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
  const [fileAttachments, setFileAttachments] = useState<File[]>([]);
  const [imageAttachments, setImageAttachments] = useState<File[]>([]);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const [userInfo] = useAtom<UserInfo>(user_info);
  const [showChat, setShowChat] = useState(true);
  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;

  // Socket.io connection for real-time messaging
  const { joinConversation, leaveConversation } = useSocket({
    userId: userInfo?.id?.toString(),
    onNewMessage: useCallback((message: any) => {
      console.log('📩 [SOCKET] Received new message:', message);

      // Skip messages sent by the current user (to avoid duplicates)
      if (message.metadata?.sender_id === userInfo?.id) {
        console.log('📩 [SOCKET] Skipping own message to avoid duplicate');
        return;
      }

      // Only add message if we're in the same conversation
      if (activeConversation && message.conversation_id === activeConversation.id) {
        setMessages((prevMessages) => {
          // Check if message already exists
          const messageExists = prevMessages.some(m => m.id === message.id);
          if (messageExists) return prevMessages;

          // Determine sender based on metadata
          let sender: "user" | "assistant" | "collaborator";
          if (message.metadata?.message_type === 'team-chat') {
            sender = "collaborator"; // It's always from another user since we skip own messages
          } else {
            sender = message.role === "user" ? "user" : "assistant";
          }

          const newMessage: Message = {
            id: message.id,
            text: message.content,
            sender,
            timestamp: new Date(message.timestamp),
            type: message.role === "assistant" ? "insight" : "text",
            senderName: message.metadata?.sender_name,
            attachments: message.metadata?.attachments || [],
            status: sender === "user" || sender === "collaborator" ? "delivered" : undefined
          };

          return [...prevMessages, newMessage];
        });
      }
    }, [activeConversation, userInfo?.id])
  });

  // Fetch broker agents from the same brokerage (including pending invitations)
  const fetchTeamMembers = async () => {
    try {
      // First, get user's brokerage info from team members endpoint
      const brokerageResponse = await axiosInstance.get("/chat/team/members");

      if (brokerageResponse.data.success && brokerageResponse.data.brokerage) {
        const brokerage = brokerageResponse.data.brokerage;
        setBrokerageInfo(brokerage);
        console.log('User brokerage:', brokerage);

        // Now fetch ALL broker agents (including pending) from the broker agents endpoint
        const agentsResponse = await axiosInstance.get(`/broker/agents/${brokerage.id}`);

        if (agentsResponse.data.success) {
          // Transform broker agents to match collaborator structure
          const agentCollaborators = agentsResponse.data.agents.map((agent: any) => {
            // Parse the name (could include "(Pending Invitation)" suffix)
            const nameWithoutSuffix = agent.agent_full_name.replace(' (Pending Invitation)', '');
            const nameParts = nameWithoutSuffix.split(' ');

            return {
              collaborator_id: agent.user_id || agent.id, // Use user_id if authenticated, otherwise agent id
              invitee: {
                collaborator_first_name: nameParts[0] || '',
                collaborator_last_name: nameParts.slice(1).join(' ') || '',
                collaborator_email: agent.agent_email,
                collaborator_phone: agent.agent_phone || ''
              },
              role: agent.member_status,
              status: agent.isAuthenticated ? 'active' : 'pending',
              isPending: !agent.isAuthenticated,
              isAuthenticated: agent.isAuthenticated,
              invitationSent: agent.invitationSent,
              created_on: agent.created_at || new Date().toISOString()
            };
          });

          console.log(`✅ Fetched ${agentCollaborators.length} broker agents (including pending)`);
          setCollaborators(agentCollaborators);
        }
      }
    } catch (error: any) {
      console.log("Error fetching broker agents:", error.response?.data?.message);
      // Fallback to old collaborators API if broker agents fails
      try {
        const fallbackResponse = await axiosInstance.get("/collaborators");
        setCollaborators(fallbackResponse.data.data.collaborators);
      } catch (fallbackError) {
        console.log("Fallback collaborators fetch also failed");
      }
    }
  };

  useEffect(() => {
    const initializeChat = async () => {
      await fetchTeamMembers();
      await loadConversations();
    };
    initializeChat();
  }, []);

  // Join/leave socket conversation rooms when active conversation changes
  useEffect(() => {
    if (activeConversation) {
      console.log('🔌 [SOCKET] Joining conversation room:', activeConversation.id);
      joinConversation(activeConversation.id);

      // Cleanup: leave room when conversation changes
      return () => {
        console.log('🔌 [SOCKET] Leaving conversation room:', activeConversation.id);
        leaveConversation(activeConversation.id);
      };
    }
  }, [activeConversation, joinConversation, leaveConversation]);

  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (!backdropExists) {
              setShowChat(false);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const getConversationStyling = (agentType: string) => {
    const agent = aiAgents.find(a => a.id === agentType);
    if (agent) return agent;
    
    switch (agentType) {
      case 'human-collaborator':
        return { icon: faUsers, color: 'warning', gradient: 'linear-gradient(135deg, #ffc107, #fd7e14)' };
      default:
        return { icon: faBolt, color: 'secondary', gradient: 'linear-gradient(135deg, #6c757d, #495057)' };
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() && fileAttachments.length === 0 && imageAttachments.length === 0) {
      setError("Message or attachment is required");
      return;
    }

    if (!activeConversation) {
      setError("Please select a conversation or start a new one");
      return;
    }

    setError("");
    setLoading(true);

    const newUserMessage: Message = {
      id: messages.length + 1,
      text: inputMessage || (fileAttachments.length > 0 || imageAttachments.length > 0 ? `📎 Sent ${fileAttachments.length + imageAttachments.length} file(s)` : ""),
      sender: "user",
      timestamp: new Date(),
      type: "text",
      status: "sending"
    };

    setMessages((prevMessages) => [...prevMessages, newUserMessage]);
    const messageToSend = inputMessage;
    const filesToSend = [...fileAttachments];
    const imagesToSend = [...imageAttachments];

    setInputMessage("");
    setFileAttachments([]);
    setImageAttachments([]);

    try {
      if (activeConversation.agentType === 'human-collaborator') {
        let response: any;

        // Send message to human collaborator with file attachments
        if (filesToSend.length > 0 || imagesToSend.length > 0) {
          const formData = new FormData();
          formData.append('conversation_id', activeConversation.id.toString());
          formData.append('message', messageToSend);
          formData.append('participant_id', activeConversation.participantId || '');

          // Add all files
          filesToSend.forEach((file) => {
            formData.append('files', file);
          });

          // Add all images
          imagesToSend.forEach((image) => {
            formData.append('images', image);
          });

          // Don't set Content-Type manually - axios will set it with the correct boundary
          response = await axiosInstance.post('/chat/team/chat', formData);
        } else {
          // Send text-only message
          response = await axiosInstance.post('/chat/team/chat', {
            conversation_id: activeConversation.id.toString(),
            message: messageToSend,
            participant_id: activeConversation.participantId,
          });
        }

        // Update the temporary message with real data from backend response
        if (response.data.messageData) {
          const messageTimestamp = new Date(response.data.messageData.timestamp);

          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            const lastMessageIndex = updatedMessages.length - 1;
            updatedMessages[lastMessageIndex] = {
              id: response.data.messageData.id,
              text: response.data.messageData.content,
              sender: "user",
              timestamp: messageTimestamp,
              type: "text",
              attachments: response.data.messageData.metadata?.attachments || [],
              status: "sent"
            };
            return updatedMessages;
          });

          // Update the collaborator's last message in the sidebar
          if (activeConversation?.participantId) {
            setCollaborators(prev =>
              prev.map(m =>
                m.collaborator_id === activeConversation.participantId
                  ? {
                      ...m,
                      lastMessage: response.data.messageData.content,
                      lastMessageTime: messageTimestamp
                    }
                  : m
              )
            );

            // Also update conversationHistory for team member conversations
            setConversationHistory(prev => {
              const existingConv = prev.find(c => c.id === activeConversation.id);

              if (existingConv) {
                // Update existing conversation
                return prev.map(conv =>
                  conv.id === activeConversation.id
                    ? {
                        ...conv,
                        lastMessage: response.data.messageData.content,
                        timestamp: messageTimestamp
                      }
                    : conv
                );
              } else {
                // Add new conversation if it doesn't exist
                const newConv: EnhancedConversation = {
                  id: activeConversation.id,
                  name: activeConversation.name,
                  lastMessage: response.data.messageData.content,
                  timestamp: messageTimestamp,
                  unread: false,
                  agentType: 'human-collaborator',
                  participantId: activeConversation.participantId,
                  participantName: activeConversation.participantName
                };
                return [newConv, ...prev];
              }
            });
          }
        }

        toast.success("Message sent to team member");
        setLoading(false);
      } else {
        // Use streaming for AI agents (file attachments not yet supported for AI)
        if (filesToSend.length > 0 || imagesToSend.length > 0) {
          toast.error("File attachments are only supported for team chat");
          setLoading(false);
          return;
        }
        await handleStreamingMessage(messageToSend);
      }
    } catch (error: any) {
      console.error('Chat error:', error);
      toast.error(error.response?.data?.error || "Failed to send message");
      setLoading(false);
    }
  };

  // PRODUCTION READY: Streaming message handler
  const handleStreamingMessage = async (messageToSend: string) => {
    try {
      const now = new Date();

      // Update conversation timestamp immediately when user sends message
      if (activeConversation) {
        setConversationHistory(prev =>
          prev.map(conv =>
            conv.id === activeConversation.id
              ? { ...conv, lastMessage: messageToSend, timestamp: now }
              : conv
          )
        );
      }

      // Create a placeholder message for the AI response
      const aiResponsePlaceholder: Message = {
        id: Date.now(),
        text: "Thinking...",
        sender: "assistant",
        timestamp: now,
        type: "insight"
      };

      // Add placeholder to messages
      setMessages((prevMessages) => [...prevMessages, aiResponsePlaceholder]);

      // Prepare request payload
      const payload = {
        conversation_id: activeConversation!.id.toString(),
        message: messageToSend,
        agent_type: activeConversation!.agentType,
        system_prompt: activeConversation!.systemPrompt,
      };

      // Use fetch for streaming
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${axiosInstance.defaults.baseURL}chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read the streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  
                  if (data.type === 'chunk' && data.partial_response) {
                    fullText = data.partial_response;
                    
                    // Update the message with streaming text
                    setMessages((prevMessages) => {
                      const newMessages = [...prevMessages];
                      const targetIndex = newMessages.findIndex(m => m.id === aiResponsePlaceholder.id);
                      if (targetIndex >= 0) {
                        newMessages[targetIndex] = {
                          ...newMessages[targetIndex],
                          text: fullText
                        };
                      }
                      return newMessages;
                    });
                    
                  } else if (data.type === 'stream_complete') {
                    setLoading(false);
                    return;
                  }
                } catch (parseError) {
                  console.error('Error parsing SSE data:', parseError);
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }

      setLoading(false);

    } catch (error: any) {
      console.error('Error in streaming handler:', error);
      
      // Fallback to regular axios request if streaming fails
      try {
        const payload = {
          conversation_id: activeConversation!.id.toString(),
          message: messageToSend,
          agent_type: activeConversation!.agentType,
          system_prompt: activeConversation!.systemPrompt,
        };

        const axiosResponse = await axiosInstance.post('/chat/chat', payload);
        
        // Update the placeholder with the actual response
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          const targetIndex = newMessages.findIndex(m => m.id === Date.now());
          if (targetIndex >= 0) {
            newMessages[targetIndex] = {
              ...newMessages[targetIndex],
              text: axiosResponse.data.response || "No response received"
            };
          }
          return newMessages;
        });
      } catch (fallbackError) {
        toast.error('Failed to send message');
        // Remove the placeholder AI message on error
        setMessages((prevMessages) => 
          prevMessages.filter(m => m.id !== Date.now())
        );
      }
      
      setLoading(false);
    }
  };

  const getChatHistory = async (conversationId: string | number) => {
    try {
      console.log('📖 [HISTORY] Getting chat history for:', conversationId);
      setLoadingChatHistory(true);
      const conversation = conversationHistory.find(c => c.id === conversationId);
      console.log('📖 [HISTORY] Found conversation in history:', !!conversation);
      setActiveConversation(conversation || null);

      // Socket will automatically join the conversation room via useEffect

      // Use simplified API to get messages
      console.log('📡 [HISTORY] Fetching messages from API:', `/chat/conversations/${conversationId}/messages`);
      const response = await axiosInstance.get(`/chat/conversations/${conversationId}/messages`);
      console.log('📡 [HISTORY] Received messages:', {
        count: response.data.messages?.length || 0,
        messages: response.data.messages
      });

      const newMessages: Message[] = [];
      response.data.messages?.forEach((message: any) => {
        // For team chat messages, check sender_id to determine if it's from current user or collaborator
        let sender: "user" | "assistant" | "collaborator";
        if (message.metadata?.message_type === 'team-chat') {
          // Team chat: check if sender_id matches current user
          sender = message.metadata?.sender_id === userInfo?.id ? "user" : "collaborator";
        } else {
          // Regular AI chat
          sender = message.role === "user" ? "user" : "assistant";
        }

        const newMessage: Message = {
          id: message.id,
          text: message.content,
          sender,
          timestamp: new Date(message.timestamp),
          type: message.role === "assistant" ? "insight" : "text",
          senderName: message.metadata?.sender_name || message.sender_name,
          attachments: message.metadata?.attachments || [],
          status: sender === "user" || sender === "collaborator" ? "delivered" : undefined
        };
        newMessages.push(newMessage);
      });

      setMessages(newMessages.length > 0 ? newMessages : [
        {
          id: 1,
          text: "👋 Hello! How can I help you today?",
          sender: "assistant",
          timestamp: new Date(),
          type: "text"
        }
      ]);
      setLoadingChatHistory(false);

      // Mark conversation as read
      setConversationHistory(prev =>
        prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, unread: false }
            : conv
        )
      );
    } catch (error: any) {
      console.error('Error loading chat history:', error);
      toast.error(error.response?.data?.error || "Failed to load conversation");
      setLoadingChatHistory(false);
    }
  };

  const startNewConversation = () => {
    setShowInlineSelection(true);
    setActiveConversation(null);
    setMessages([]);
    setInlineSearchQuery("");
  };

  // Load conversations from API
  const loadConversations = async () => {
    try {
      const response = await axiosInstance.get('/chat/conversations');
      console.log('📋 [CONVERSATIONS] Loaded conversations from API:', response.data.conversations);

      const apiConversations = response.data.conversations.map((conv: any) => {
        const lastMessage = conv.messages?.[0]?.content || "No messages yet";
        // Use the most recent message timestamp if available, otherwise use conversation updated_at
        const lastMessageTimestamp = conv.messages?.[0]?.timestamp
          ? new Date(conv.messages[0].timestamp)
          : new Date(conv.updated_at);

        console.log(`💬 [CONVERSATION] ${conv.id}:`, {
          agentType: conv.agent_type,
          agentId: conv.agent_id,
          title: conv.title,
          lastMessage,
          messageCount: conv._count?.messages || 0,
          lastMessageTimestamp
        });

        return {
          id: conv.id,
          name: conv.title,
          lastMessage,
          timestamp: lastMessageTimestamp,
          unread: false, // Could be calculated based on read status
          agentType: conv.agent_type,
          participantId: conv.agent_id,
          participantName: conv.agent_id ? "Team Member" : undefined,
          systemPrompt: conv.system_prompt
        };
      });
      setConversationHistory(apiConversations);

      // Merge conversation data with team members
      mergeConversationsWithMembers(apiConversations);
    } catch (error: any) {
      console.error('Error loading conversations:', error);
      // Keep dummy data as fallback
    }
  };

  // Merge conversation data with team members
  const mergeConversationsWithMembers = (conversations: EnhancedConversation[]) => {
    console.log('🔄 [MERGE] Merging conversations with members:', {
      conversationCount: conversations.length,
      conversations: conversations.map(c => ({ id: c.id, participantId: c.participantId, lastMessage: c.lastMessage }))
    });

    setCollaborators(prevMembers => {
      return prevMembers.map(member => {
        // Find conversation with this member
        const conversation = conversations.find(
          conv => conv.agentType === 'human-collaborator' && conv.participantId === member.collaborator_id
        );

        console.log(`🔍 [MERGE] Member ${member.collaborator_id}:`, {
          found: !!conversation,
          conversationId: conversation?.id,
          lastMessage: conversation?.lastMessage
        });

        if (conversation) {
          return {
            ...member,
            conversationId: conversation.id.toString(),
            lastMessage: conversation.lastMessage,
            lastMessageTime: conversation.timestamp,
            unreadCount: conversation.unread ? 1 : 0
          };
        }

        return member;
      });
    });
  };

  const handleSelectAgent = async (agent: AgentType) => {
    try {
      // Create conversation via API
      const response = await axiosInstance.post('/chat/conversations', {
        agent_type: agent.id,
        title: `${agent.name} Session`
      });

      const newConversation: EnhancedConversation = {
        id: response.data.conversation.id,
        name: response.data.conversation.title,
        lastMessage: "New conversation started",
        timestamp: new Date(),
        unread: false,
        agentType: agent.id,
        systemPrompt: agent.systemPrompt
      };

      setConversationHistory(prev => [newConversation, ...prev]);
      setActiveConversation(newConversation);
      setMessages([
        {
          id: 1,
          text: `👋 Hello! I'm your **${agent.name}**. ${agent.description}. How can I assist you today?`,
          sender: "assistant",
          timestamp: new Date(),
          type: "text"
        },
      ]);
      setShowAgentModal(false);
      // Socket will automatically handle room management via useEffect
    } catch (error: any) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to create conversation');
    }
  };

  const handleSelectCollaborator = async (collaborator: Collaborator) => {
    try {
      const fullName = `${collaborator.invitee.collaborator_first_name} ${collaborator.invitee.collaborator_last_name}`;

      console.log('👤 [SELECT] Selecting collaborator:', {
        collaboratorId: collaborator.collaborator_id,
        existingConversationId: collaborator.conversationId,
        fullName
      });

      let conversationId: string | number = collaborator.conversationId || '';

      // If no existing conversation, create one
      if (!conversationId) {
        console.log('🆕 [SELECT] No existing conversation, creating new one');
        const response = await axiosInstance.post('/chat/conversations', {
          agent_type: 'human-collaborator',
          agent_id: collaborator.collaborator_id,
          title: `Chat with ${fullName}`
        });
        conversationId = response.data.conversation.id;
        console.log('✅ [SELECT] Created/found conversation:', conversationId);

        // Update the collaborator with the new conversation ID
        setCollaborators(prev =>
          prev.map(m =>
            m.collaborator_id === collaborator.collaborator_id
              ? { ...m, conversationId: conversationId as string }
              : m
          )
        );

        // Add the new team conversation to conversationHistory
        const newTeamConversation: EnhancedConversation = {
          id: conversationId,
          name: `Chat with ${fullName}`,
          lastMessage: "Start chatting with your team member",
          timestamp: new Date(),
          unread: false,
          agentType: 'human-collaborator',
          participantId: collaborator.collaborator_id,
          participantName: fullName
        };
        setConversationHistory(prev => [newTeamConversation, ...prev]);
      } else {
        console.log('♻️ [SELECT] Using existing conversation:', conversationId);
      }

      // Ensure conversationId is valid before proceeding
      if (!conversationId) {
        throw new Error('Failed to get or create conversation');
      }

      console.log('🎯 [SELECT] About to load conversation:', {
        conversationId,
        collaboratorId: collaborator.collaborator_id,
        hasExistingMessages: !!collaborator.lastMessage
      });

      const conversation: EnhancedConversation = {
        id: conversationId,
        name: `Chat with ${fullName}`,
        lastMessage: collaborator.lastMessage || "Loading messages...",
        timestamp: collaborator.lastMessageTime || new Date(),
        unread: false,
        agentType: 'human-collaborator',
        participantId: collaborator.collaborator_id,
        participantName: fullName
      };

      // Update conversation history if not already present
      setConversationHistory(prev => {
        const exists = prev.find(c => c.id === conversation.id);
        if (exists) {
          console.log('♻️ [SELECT] Conversation already in history');
          return prev;
        }
        console.log('🆕 [SELECT] Adding conversation to history');
        return [conversation, ...prev];
      });

      setActiveConversation(conversation);
      setShowAgentModal(false);

      // Load all messages for this conversation
      console.log('📥 [SELECT] Loading messages for conversation:', conversationId);
      await getChatHistory(conversationId);

      // Socket will automatically join the conversation room via useEffect
    } catch (error: any) {
      console.error('Error creating team conversation:', error);
      toast.error('Failed to create team conversation');
    }
  };

  // Combine AI conversations and team member conversations into a unified list
  const allConversations = useMemo(() => {
    // Get AI agent conversations from conversationHistory
    const aiConvs = conversationHistory
      .filter(conv => conv.agentType !== 'human-collaborator')
      .map(conv => ({
        ...conv,
        type: 'ai' as const,
        sortTime: new Date(conv.timestamp).getTime()
      }));

    // Get team member conversations from collaborators
    const teamConvs = collaborators
      .filter(member => !!member.conversationId)
      .map(member => {
        const fullName = `${member.invitee.collaborator_first_name} ${member.invitee.collaborator_last_name}`;
        return {
          id: member.conversationId || '',
          name: fullName,
          lastMessage: member.lastMessage || member.invitee.collaborator_email,
          timestamp: member.lastMessageTime || new Date(),
          agentType: 'human-collaborator' as const,
          participantId: member.collaborator_id,
          participantName: fullName,
          type: 'team' as const,
          sortTime: member.lastMessageTime ? new Date(member.lastMessageTime).getTime() : 0,
          memberData: member
        };
      });

    // Combine and sort by most recent
    return [...aiConvs, ...teamConvs]
      .filter(conv => {
        // Apply search filter
        const nameMatch = conv.name.toLowerCase().includes(searchQuery.toLowerCase());
        const messageMatch = conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase());
        return nameMatch || messageMatch;
      })
      .sort((a, b) => b.sortTime - a.sortTime);
  }, [conversationHistory, collaborators, searchQuery]);

  // Image Preview Component with Authentication - Memoized to prevent re-loading
  const ImagePreview = React.memo(({ downloadUrl, attachment, fileSize, isUser }: any) => {
    const [imageSrc, setImageSrc] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const hasLoadedRef = useRef(false);

    // Load image only once
    useEffect(() => {
      let isMounted = true;

      // Prevent multiple loads - only load if we haven't loaded yet
      if (!hasLoadedRef.current && !imageSrc) {
        hasLoadedRef.current = true;

        const loadImage = async () => {
          try {
            // If it's a direct S3 URL, use it directly without proxying through API
            const isS3Url = downloadUrl.includes('.s3.') || downloadUrl.startsWith('https://') && downloadUrl.includes('amazonaws.com');

            if (isS3Url) {
              // Direct S3 URL - use it directly
              if (isMounted) {
                setImageSrc(downloadUrl);
                setLoading(false);
              }
            } else {
              // Legacy format - fetch through API
              const response = await axiosInstance.get(downloadUrl, {
                responseType: 'blob'
              });
              if (isMounted) {
                const blob = new Blob([response.data], { type: attachment.mimetype });
                const blobUrl = URL.createObjectURL(blob);
                setImageSrc(blobUrl);
                setLoading(false);
              }
            }
          } catch (error) {
            console.error('Error loading image:', error);
            if (isMounted) {
              setLoading(false);
              setError(true);
              hasLoadedRef.current = false; // Allow retry on error
            }
          }
        };

        loadImage();
      }

      return () => {
        isMounted = false;
      };
    }, [downloadUrl, attachment.mimetype, imageSrc]);

    // Separate cleanup effect for blob URL (only revoke blob URLs, not S3 URLs)
    useEffect(() => {
      return () => {
        // Only revoke blob URLs (created from API responses), not direct S3 URLs
        if (imageSrc && imageSrc.startsWith('blob:')) {
          URL.revokeObjectURL(imageSrc);
        }
      };
    }, [imageSrc]);

    // Memoize handlers inside ImagePreview to prevent recreation
    const handleDownload = useCallback(async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        const response = await axiosInstance.get(downloadUrl, {
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: attachment.mimetype });
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = attachment.originalName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        toast.error('Failed to download attachment');
      }
    }, [downloadUrl, attachment.mimetype, attachment.originalName]);

    const handleOpenInNewTab = useCallback(async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        const response = await axiosInstance.get(downloadUrl, {
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: attachment.mimetype });
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      } catch (error) {
        console.error('Error opening attachment:', error);
        toast.error('Failed to open file');
      }
    }, [downloadUrl, attachment.mimetype]);

    return (
      <div className="position-relative" style={{ maxWidth: "300px" }}>
        {loading ? (
          <div
            className={classNames(
              "d-flex align-items-center justify-content-center rounded",
              isUser ? "bg-white-10" : "bg-secondary-subtle"
            )}
            style={{ minHeight: "150px" }}
          >
            <Spinner animation="border" size="sm" className={isUser ? "text-white" : ""} />
          </div>
        ) : imageSrc ? (
          <>
            <img
              src={imageSrc}
              alt={attachment.originalName}
              className="img-fluid rounded cursor-pointer"
              style={{
                maxHeight: "200px",
                objectFit: "contain",
                width: "100%"
              }}
              onClick={handleOpenInNewTab}
              title="Click to open in new tab"
            />
            <div
              className="position-absolute top-0 end-0 m-2"
              style={{ background: "rgba(0,0,0,0.7)", borderRadius: "8px", padding: "4px 8px" }}
            >
              <button
                className="btn btn-sm text-white p-0"
                onClick={handleDownload}
                title="Download"
                style={{ background: "transparent", border: "none" }}
              >
                <FontAwesomeIcon icon={faDownload} size="sm" />
              </button>
            </div>
          </>
        ) : null}
        {error && (
          <div
            className={classNames(
              "p-3 rounded text-center",
              isUser ? "bg-white-10 text-white" : "bg-secondary-subtle"
            )}
          >
            <FontAwesomeIcon icon={faImage} size="2x" className={isUser ? "text-white-50" : "text-secondary"} />
            <div className="mt-2 small">
              Image unavailable
              <div className="mt-1" style={{ fontSize: "0.7rem" }}>
                (Legacy attachment no longer accessible)
              </div>
            </div>
          </div>
        )}
        <div className="p-2">
          <small
            className={classNames(
              isUser ? "text-white-75" : "text-muted"
            )}
            style={{ fontSize: "0.75rem" }}
          >
            {attachment.originalName} • {fileSize} KB
          </small>
        </div>
      </div>
    );
  }, (prevProps, nextProps) => {
    // Return true if props are equal (to PREVENT re-render)
    return (
      prevProps.downloadUrl === nextProps.downloadUrl &&
      prevProps.attachment.originalName === nextProps.attachment.originalName &&
      prevProps.attachment.mimetype === nextProps.attachment.mimetype &&
      prevProps.fileSize === nextProps.fileSize &&
      prevProps.isUser === nextProps.isUser
    );
  });

  // Memoized message component to prevent re-renders and image reloading
  const MessageItem = React.memo(({ message, isFirstInGroup = true, isLastInGroup = true }: { message: Message; isFirstInGroup?: boolean; isLastInGroup?: boolean }) => {
    const isUser = message.sender === "user";
    const isCollaborator = message.sender === "collaborator";
    const isInsight = message.type === "insight" || message.type === "analysis";

    const displayText = message.text || "No text";

    // Memoized download handler factory
    const createDownloadHandler = useCallback((downloadUrl: string, mimetype: string, originalName: string) => {
      return async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
          const response = await axiosInstance.get(downloadUrl, {
            responseType: 'blob'
          });

          const blob = new Blob([response.data], { type: mimetype });
          const blobUrl = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = originalName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
          console.error('Error downloading attachment:', error);
          toast.error('Failed to download attachment');
        }
      };
    }, []);

    // Memoized open in new tab handler factory
    const createOpenInNewTabHandler = useCallback((downloadUrl: string, mimetype: string) => {
      return async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
          const response = await axiosInstance.get(downloadUrl, {
            responseType: 'blob'
          });

          const blob = new Blob([response.data], { type: mimetype });
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');

          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
          console.error('Error opening attachment:', error);
          toast.error('Failed to open file');
        }
      };
    }, []);

    // Calculate bubble border radius based on position in group
    const getBorderRadius = () => {
      if (isUser) {
        return "18px 18px 4px 18px"; // User messages: sharp corner bottom-right
      } else {
        return "18px 18px 18px 4px"; // Other messages: sharp corner bottom-left
      }
    };

    // Check if message is image-only (has attachments and text is just a placeholder)
    const hasAttachments = message.attachments && message.attachments.length > 0;
    const isPlaceholderText = message.text.includes('[Attachment]') ||
                             message.text.includes('📎 Sent') ||
                             message.text.trim() === '';
    const isImageOnly = hasAttachments && isPlaceholderText &&
                       message.attachments?.some(a => a.type === 'image');

    return (
      <div
        key={`${message.id}-${message.lastUpdated || message.timestamp?.getTime() || 0}`}
        className="position-relative"
      >
        <div
          className={classNames(
            isImageOnly ? "p-0" : "px-3 py-2",
            isUser
              ? "bg-primary text-white"
              : isCollaborator
                ? "text-dark"
                : isInsight
                  ? "bg-success-subtle"
                  : "bg-light"
          )}
          style={{
            borderRadius: getBorderRadius(),
            border: "none",
            boxShadow: isUser ? "none" : "0 1px 2px rgba(0,0,0,0.1)",
            backgroundColor: isUser ? "#007bff" : isCollaborator ? "#f0f0f0" : isInsight ? "#d1e7dd" : "#f8f9fa",
            transition: "box-shadow 0.2s ease"
          }}
        >
          {/* Store the computed value for use in the rest of the component */}
          {(() => {
            (message as any)._isImageOnly = isImageOnly;
            return null;
          })()}

              {isInsight && !isCollaborator && (
                <div className="d-flex align-items-center mb-2 pb-2 border-bottom border-success-subtle">
                  <div className="d-flex align-items-center">
                    <div 
                      className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                      style={{ width: "20px", height: "20px", background: "linear-gradient(135deg, #198754, #20c997)" }}
                    >
                      <FontAwesomeIcon icon={faLightbulb} className="text-white" size="xs" />
                    </div>
                    <small className="text-success fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                      AI INSIGHT
                    </small>
                  </div>
                </div>
              )}

              {/* Only show text if it's not a placeholder for image-only messages */}
              {!((message as any)._isImageOnly) && (
                <div
                  className={classNames(
                    "lh-base",
                    isUser ? "text-white" : isCollaborator ? "text-warning-emphasis" : "text-body"
                  )}
                  style={{ fontSize: "0.95rem" }}
                >
                  {displayText.includes("**") ? (
                    <div dangerouslySetInnerHTML={{
                      __html: displayText
                        .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
                        .replace(/\n/g, '<br>')
                    }} />
                  ) : (
                    displayText
                  )}
                </div>
              )}

              {/* Attachment Display */}
              {message.attachments && message.attachments.length > 0 && (
                <div className={(message as any)._isImageOnly ? "" : "mt-2"}>
                  {message.attachments.map((attachment, index) => {
                    // Prefer direct S3 URL from new format, fall back to API endpoint for legacy files
                    const downloadUrl = attachment.url || attachment.s3Location ||
                      `${axiosInstance.defaults.baseURL}chat/attachments/${attachment.s3Key || attachment.savedFileName || attachment.path?.split('/').pop()}`;

                    const isImage = attachment.type === 'image';
                    const isPDF = attachment.mimetype === 'application/pdf';
                    const fileSize = (attachment.size / 1024).toFixed(1); // KB
                    const fileName = attachment.filename || attachment.originalName || 'Attachment';

                    // For S3 URLs, we can use them directly without proxying through backend
                    const isS3Direct = !!(attachment.url || attachment.s3Location);

                    // Create stable handlers for this attachment
                    const handleDownload = createDownloadHandler(downloadUrl, attachment.mimetype, fileName);
                    const handleOpenInNewTab = createOpenInNewTabHandler(downloadUrl, attachment.mimetype);

                    return (
                      <div
                        key={index}
                        className={classNames(
                          "mb-2 overflow-hidden",
                          // Only add background/padding for non-images
                          isImage ? "" : "rounded p-2",
                          isImage ? "" : (isUser ? "bg-white-10" : "bg-light")
                        )}
                      >
                        {/* Inline Image Preview */}
                        {isImage && (
                          <ImagePreview
                            downloadUrl={downloadUrl}
                            attachment={attachment}
                            fileSize={fileSize}
                            isUser={isUser}
                          />
                        )}

                        {/* PDF Preview Card */}
                        {isPDF && (
                          <div
                            className="p-3 d-flex align-items-center justify-content-between"
                            style={{ cursor: "pointer" }}
                            onClick={handleOpenInNewTab}
                          >
                            <div className="d-flex align-items-center flex-grow-1">
                              <div
                                className={classNames(
                                  "rounded d-flex align-items-center justify-content-center me-3",
                                  isUser ? "bg-white-20" : "bg-primary-subtle"
                                )}
                                style={{ width: "48px", height: "48px", flexShrink: 0 }}
                              >
                                <FontAwesomeIcon
                                  icon={faPaperclip}
                                  className={isUser ? "text-white" : "text-primary"}
                                  size="lg"
                                />
                              </div>
                              <div className="flex-grow-1 min-w-0">
                                <div
                                  className={classNames(
                                    "small fw-semibold mb-1",
                                    isUser ? "text-white" : "text-dark"
                                  )}
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  {attachment.originalName}
                                </div>
                                <div
                                  className={classNames(
                                    "text-xs",
                                    isUser ? "text-white-75" : "text-muted"
                                  )}
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  PDF • {fileSize} KB • Click to view
                                </div>
                              </div>
                            </div>
                            <button
                              className={classNames(
                                "btn btn-sm ms-2",
                                isUser ? "btn-outline-light" : "btn-outline-primary"
                              )}
                              onClick={handleDownload}
                              title="Download"
                            >
                              <FontAwesomeIcon icon={faDownload} size="sm" />
                            </button>
                          </div>
                        )}

                        {/* Other File Types */}
                        {!isImage && !isPDF && (
                          <div
                            className="p-3"
                            style={{ cursor: "pointer" }}
                            onClick={handleDownload}
                          >
                            <div className="d-flex align-items-center">
                              <div className="me-2">
                                <FontAwesomeIcon
                                  icon={faPaperclip}
                                  className={isUser ? "text-white-75" : "text-primary"}
                                  size="lg"
                                />
                              </div>
                              <div className="flex-grow-1">
                                <div
                                  className={classNames(
                                    "small fw-semibold",
                                    isUser ? "text-white" : "text-dark"
                                  )}
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  {attachment.originalName}
                                </div>
                                <div
                                  className={classNames(
                                    "text-xs",
                                    isUser ? "text-white-50" : "text-muted"
                                  )}
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  {fileSize} KB
                                </div>
                              </div>
                              <div>
                                <FontAwesomeIcon
                                  icon={faDownload}
                                  className={isUser ? "text-white-50" : "text-muted"}
                                  size="sm"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Timestamp and status - WhatsApp style */}
              {message.timestamp && isLastInGroup && (
                <div className="d-flex align-items-center justify-content-end mt-1" style={{ gap: "4px" }}>
                  <small
                    className={classNames(
                      isUser ? "text-white-75" : "text-muted"
                    )}
                    style={{ fontSize: "0.65rem", opacity: 0.7 }}
                  >
                    {formatTime(message.timestamp)}
                  </small>
                  {isUser && message.status && (
                    <span style={{ fontSize: "0.75rem", opacity: 0.8 }}>
                      {message.status === "sending" && (
                        <FontAwesomeIcon
                          icon={faClock}
                          className="text-white-75"
                          size="xs"
                        />
                      )}
                      {message.status === "sent" && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-white-75"
                          size="xs"
                        />
                      )}
                      {message.status === "delivered" && (
                        <FontAwesomeIcon
                          icon={faCheckDouble}
                          className="text-white"
                          size="xs"
                        />
                      )}
                      {message.status === "read" && (
                        <FontAwesomeIcon
                          icon={faCheckDouble}
                          className="text-info"
                          size="xs"
                        />
                      )}
                    </span>
                  )}
                </div>
              )}
        </div>
      </div>
    );
  }, (prevProps, nextProps) => {
    // Custom comparison: only re-render if message content actually changed
    return (
      prevProps.message.id === nextProps.message.id &&
      prevProps.message.text === nextProps.message.text &&
      prevProps.message.lastUpdated === nextProps.message.lastUpdated &&
      prevProps.isFirstInGroup === nextProps.isFirstInGroup &&
      prevProps.isLastInGroup === nextProps.isLastInGroup &&
      JSON.stringify(prevProps.message.attachments) === JSON.stringify(nextProps.message.attachments)
    );
  });


  // Group messages by sender for better visual grouping (like WhatsApp/iMessage)
  const groupedMessages = useMemo(() => {
    const groups: Message[][] = [];
    let currentGroup: Message[] = [];

    messages.forEach((msg, idx) => {
      const prevMsg = idx > 0 ? messages[idx - 1] : null;
      const timeDiff = prevMsg ? (msg.timestamp?.getTime() || 0) - (prevMsg.timestamp?.getTime() || 0) : 0;

      // Start new group if sender changes or more than 5 minutes between messages
      if (!prevMsg || msg.sender !== prevMsg.sender || timeDiff > 5 * 60 * 1000) {
        if (currentGroup.length > 0) {
          groups.push([...currentGroup]);
        }
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  }, [messages]);

  // Memoize the rendered messages to prevent re-rendering all messages on input changes
  const renderedMessages = useMemo(() => {
    return groupedMessages.map((group, groupIdx) => {
      const isUser = group[0].sender === "user";
      const isCollaborator = group[0].sender === "collaborator";

      return (
        <div key={`group-${groupIdx}`} className="d-flex mb-3" style={{ gap: "8px", alignItems: "flex-start" }}>
          {/* Avatar for collaborator messages */}
          {isCollaborator && (
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "32px",
                height: "32px",
                backgroundColor: "#e0e0e0",
                color: "#666",
                fontSize: "14px",
                fontWeight: 500
              }}
            >
              {group[0].senderName?.charAt(0).toUpperCase() || "?"}
            </div>
          )}

          {/* Message group container */}
          <div className="d-flex flex-column" style={{ maxWidth: "65%", gap: "2px", marginLeft: isUser ? "auto" : "0", marginRight: isUser ? "0" : "auto" }}>
            {/* Sender name for collaborator */}
            {isCollaborator && group[0].senderName && (
              <small className="text-muted ms-2" style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
                {group[0].senderName}
              </small>
            )}

            {/* Messages in group */}
            {group.map((message, msgIdx) => (
              <MessageItem key={message.id} message={message} isFirstInGroup={msgIdx === 0} isLastInGroup={msgIdx === group.length - 1} />
            ))}
          </div>

          {/* Spacer for user messages */}
          {isUser && <div style={{ width: "32px" }} className="flex-shrink-0" />}
        </div>
      );
    });
  }, [groupedMessages]);

  const AgentSelectionModal = React.memo(() => {
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Memoize filtered results to prevent re-calculations
    const filteredAiAgents = useMemo(() =>
      aiAgents.filter(agent =>
        agent.name.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(modalSearchQuery.toLowerCase())
      ),
      [modalSearchQuery]
    );

    const filteredCollaborators = useMemo(() =>
      collaborators.filter(collaborator =>
        collaborator.invitee.collaborator_first_name?.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
        collaborator.invitee.collaborator_last_name?.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
        collaborator.invitee.collaborator_email?.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
        collaborator.role?.toLowerCase().includes(modalSearchQuery.toLowerCase())
      ),
      [modalSearchQuery, collaborators]
    );

    const hasResults = filteredAiAgents.length > 0 || filteredCollaborators.length > 0;

    // Memoize handlers to prevent re-creation
    const handleClose = useCallback(() => {
      setShowAgentModal(false);
      setModalSearchQuery("");
    }, []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setModalSearchQuery(e.target.value);
    }, []);

    const handleClearSearch = useCallback(() => {
      setModalSearchQuery("");
      searchInputRef.current?.focus();
    }, []);

    const onSelectAgent = useCallback((agent: AgentType) => {
      handleSelectAgent(agent);
      handleClose();
    }, []);

    const onSelectCollaborator = useCallback((collaborator: Collaborator) => {
      handleSelectCollaborator(collaborator);
      handleClose();
    }, []);

    // Keep focus on search input
    useEffect(() => {
      if (showAgentModal && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    }, [showAgentModal]);

    return (
      <>
        <Modal
          show={showAgentModal}
          onHide={handleClose}
          size="lg"
          backdrop={true}
          animation={false}
          style={{ marginTop: "5vh" }}
        >
          <Modal.Header className="border-0 pb-2 d-flex align-items-center">
            <Modal.Title className="flex-grow-1">Start New Conversation</Modal.Title>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleClose}
              className="rounded-pill px-3 ms-2"
              style={{ fontSize: "0.875rem" }}
            >
              Close
            </Button>
          </Modal.Header>
          <Modal.Body style={{ minHeight: "500px", maxHeight: "70vh", overflowY: "auto" }}>
          {/* Search Input - Fixed at top */}
          <div className="mb-4" style={{ position: "sticky", top: "-1rem", backgroundColor: "white", zIndex: 10, paddingTop: "1rem", marginTop: "-1rem" }}>
            <InputGroup>
              <InputGroup.Text className="bg-white border-end-0">
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                ref={searchInputRef}
                type="text"
                placeholder="Search AI agents or team members..."
                value={modalSearchQuery}
                onChange={handleSearchChange}
                className="border-start-0 ps-0"
                style={{
                  boxShadow: "none",
                  fontSize: "0.95rem"
                }}
              />
              {modalSearchQuery && (
                <Button
                  variant="link"
                  className="text-muted"
                  onClick={handleClearSearch}
                  style={{ textDecoration: "none" }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              )}
            </InputGroup>
          </div>

          {/* No Results Message */}
          {!hasResults && modalSearchQuery && (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faSearch} className="text-muted mb-3" size="3x" style={{ opacity: 0.3 }} />
              <p className="text-muted mb-1">No results found for "{modalSearchQuery}"</p>
              <small className="text-muted">Try a different search term</small>
            </div>
          )}

          {/* Results */}
          <div>
            {hasResults && (
              <Row>
                {/* AI Assistants Section */}
                {filteredAiAgents.length > 0 && (
                  <Col xs={12}>
                    <h6 className="text-muted mb-3">
                      🤖 AI Assistants
                      {modalSearchQuery && (
                        <span className="badge bg-primary ms-2" style={{ fontSize: '0.7rem' }}>
                          {filteredAiAgents.length}
                        </span>
                      )}
                    </h6>
                    {filteredAiAgents.map((agent: AgentType) => (
                      <Card
                        key={agent.id}
                        className="mb-2 cursor-pointer border-0 shadow-sm"
                        onClick={() => onSelectAgent(agent)}
                        style={{
                          transition: "all 0.15s ease"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                        onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                      >
                      <Card.Body className="py-3">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle me-3 d-flex align-items-center justify-content-center shadow-sm"
                            style={{
                              width: "48px",
                              height: "48px",
                              background: agent.gradient
                            }}
                          >
                            <FontAwesomeIcon icon={agent.icon} className="text-white" />
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold">{agent.name}</h6>
                            <small className="text-muted">{agent.description}</small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </Col>
              )}

              {/* Team Members Section */}
              {filteredCollaborators.length > 0 && (
                <Col xs={12}>
                  <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
                    <h6 className="text-muted mb-0">
                      👥 Team Members
                      {brokerageInfo && (
                        <small className="text-primary ms-2">({brokerageInfo.name})</small>
                      )}
                      {modalSearchQuery && (
                        <span className="badge bg-warning ms-2" style={{ fontSize: '0.7rem' }}>
                          {filteredCollaborators.length}
                        </span>
                      )}
                    </h6>
                    {brokerageInfo && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => setShowAddTeamMemberModal(true)}
                        className="rounded-pill px-3"
                      >
                        <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                        Add
                      </Button>
                    )}
                  </div>
                  {filteredCollaborators.map((collaborator: Collaborator) => {
                    const isPending = collaborator.status === 'pending' || !collaborator.isAuthenticated;
                    return (
                      <Card
                        key={collaborator.collaborator_id}
                        className={`mb-2 border-0 shadow-sm ${isPending ? 'opacity-75' : 'cursor-pointer'}`}
                        onClick={() => !isPending && onSelectCollaborator(collaborator)}
                        style={{
                          transition: "all 0.15s ease",
                          cursor: isPending ? 'not-allowed' : 'pointer'
                        }}
                        onMouseEnter={(e) => !isPending && (e.currentTarget.style.transform = "translateY(-2px)")}
                        onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                      >
                        <Card.Body className="py-3">
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded-circle me-3 d-flex align-items-center justify-content-center shadow-sm"
                              style={{
                                width: "48px",
                                height: "48px",
                                background: isPending
                                  ? "linear-gradient(135deg, #6c757d, #adb5bd)"
                                  : "linear-gradient(135deg, #ffc107, #fd7e14)"
                              }}
                            >
                              <FontAwesomeIcon icon={faUsers} className="text-white" />
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold">
                                {collaborator.invitee.collaborator_first_name} {collaborator.invitee.collaborator_last_name}
                                {isPending && (
                                  <span className="badge bg-secondary ms-2" style={{ fontSize: '0.65rem' }}>
                                    Pending Invitation
                                  </span>
                                )}
                              </h6>
                              <small className="text-muted">{collaborator.invitee.collaborator_email}</small>
                              {collaborator.role && (
                                <>
                                  <br />
                                  <small className="text-secondary">{collaborator.role}</small>
                                </>
                              )}
                              <br />
                              {isPending ? (
                                <small className="text-muted" style={{ fontWeight: '600' }}>
                                  ● Pending activation - Cannot chat yet
                                </small>
                              ) : (
                                <small className={collaborator.status === 'online' ? 'text-success' : collaborator.status === 'away' ? 'text-warning' : 'text-muted'} style={{ fontWeight: '600' }}>
                                  ● {collaborator.status === 'online' ? 'Online' : collaborator.status === 'away' ? 'Away' : 'Offline'}
                                </small>
                              )}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </Col>
              )}

              {/* Empty State when no collaborators at all */}
              {!modalSearchQuery && collaborators.length === 0 && (
                <Col xs={12}>
                  <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
                    <h6 className="text-muted mb-0">
                      👥 Team Members
                      {brokerageInfo && (
                        <small className="text-primary ms-2">({brokerageInfo.name})</small>
                      )}
                    </h6>
                    {brokerageInfo && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => setShowAddTeamMemberModal(true)}
                        className="rounded-pill px-3"
                      >
                        <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                        Add
                      </Button>
                    )}
                  </div>
                  <div className="text-center py-4">
                    <FontAwesomeIcon icon={faUsers} className="text-muted mb-2" size="2x" />
                    <p className="text-muted mb-0">No team members available</p>
                    <small className="text-muted">Invite collaborators to start team chats</small>
                  </div>
                </Col>
              )}
            </Row>
          )}
          </div>
        </Modal.Body>
      </Modal>
      </>
    );
  });

  return (
    <>
      {(isAuthorized || showChat) && (
        <div className="h-100 d-flex flex-column" style={{ backgroundColor: "#f8f9fa" }}>
          <Row className="g-0 h-100">
            {/* Sidebar */}
            <Col xl={3} className="border-end shadow-sm" style={{ backgroundColor: "#ffffff" }}>
              <Card className="h-100 border-0 rounded-0">
                <Card.Header 
                  className="border-bottom-0 py-4"
                  style={{ 
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white"
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1 text-white fw-bold">
                        <FontAwesomeIcon icon={faMessage} className="me-2" />
                        Conversations
                      </h6>
                      <small className="text-white-75" style={{ fontSize: "0.8rem" }}>AI Agents & Team Chat</small>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => navigate('/agent-portal/ask-the-broker')}
                        className="rounded-pill px-3 fw-semibold"
                        style={{
                          fontSize: "0.8rem",
                          boxShadow: "0 2px 8px rgba(255, 193, 7, 0.3)"
                        }}
                      >
                        <FontAwesomeIcon icon={faUsers} className="me-1" />
                        Ask Broker
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        onClick={startNewConversation}
                        className="rounded-pill px-3 fw-semibold"
                        style={{
                          fontSize: "0.8rem",
                          boxShadow: "0 2px 8px rgba(255, 255, 255, 0.2)"
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        New
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  {/* Search */}
                  <div className="p-4 border-bottom">
                    <InputGroup>
                      <InputGroup.Text 
                        className="border-end-0 bg-body-secondary"
                        style={{ borderRadius: "12px 0 0 12px" }}
                      >
                        <FontAwesomeIcon icon={faSearch} className="text-muted" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search conversations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border-start-0 border-end-0 bg-body-secondary"
                        style={{ 
                          borderRadius: "0 12px 12px 0",
                          fontSize: "0.9rem"
                        }}
                      />
                    </InputGroup>
                  </div>

                  {/* Conversations List */}
                  <div className="conversation-list" style={{ height: "calc(100vh - 300px)", overflowY: "auto" }}>
                    {/* Unified Conversations List (AI + Team Members sorted by most recent) */}
                    {allConversations.map((conv) => {
                      const isTeamMember = conv.type === 'team';
                      const isPending = isTeamMember && conv.memberData ? (conv.memberData.status === 'pending' || !conv.memberData.isAuthenticated) : false;
                      const styling = getConversationStyling(conv.agentType);
                      const isActive = isTeamMember
                        ? activeConversation?.participantId === conv.participantId
                        : activeConversation?.id === conv.id;

                      const handleClick = () => {
                        if (isPending) return;
                        // Close inline selection when clicking a conversation
                        setShowInlineSelection(false);
                        if (isTeamMember && conv.memberData) {
                          handleSelectCollaborator(conv.memberData);
                        } else {
                          getChatHistory(conv.id);
                        }
                      };

                      return (
                        <div
                          key={conv.id}
                          className={classNames(
                            "p-3 border-bottom conversation-item position-relative",
                            isActive ? "bg-primary-subtle" : "",
                            isPending ? "opacity-75" : "cursor-pointer"
                          )}
                          onClick={handleClick}
                          style={{
                            borderLeft: isActive ? "4px solid #0d6efd" : "4px solid transparent",
                            transition: "all 0.2s ease-in-out",
                            background: isActive
                              ? "linear-gradient(90deg, rgba(13, 110, 253, 0.08) 0%, rgba(13, 110, 253, 0.02) 100%)"
                              : "transparent",
                            cursor: isPending ? 'not-allowed' : 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive && !isPending) {
                              e.currentTarget.style.backgroundColor = "rgba(13, 110, 253, 0.04)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive && !isPending) {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          <div className="d-flex align-items-start">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0 shadow-sm"
                              style={{
                                width: "36px",
                                height: "36px",
                                background: isPending
                                  ? "linear-gradient(135deg, #6c757d, #adb5bd)"
                                  : styling.gradient,
                                border: "2px solid rgba(255, 255, 255, 0.2)"
                              }}
                            >
                              <FontAwesomeIcon
                                icon={styling.icon}
                                className="text-white"
                                style={{ fontSize: "0.8rem" }}
                              />
                            </div>

                            <div className="flex-grow-1 min-w-0">
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <h6
                                  className="mb-0 text-truncate fw-semibold"
                                  style={{
                                    fontSize: "0.85rem",
                                    color: isActive ? "#0d6efd" : "#212529"
                                  }}
                                >
                                  {conv.name}
                                  {isPending && (
                                    <span className="badge bg-secondary ms-2" style={{ fontSize: '0.6rem' }}>
                                      Pending
                                    </span>
                                  )}
                                </h6>
                                {isTeamMember && conv.memberData && conv.memberData.unreadCount && conv.memberData.unreadCount > 0 && (
                                  <span
                                    className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      fontSize: "0.7rem",
                                      fontWeight: "600"
                                    }}
                                  >
                                    {conv.memberData.unreadCount}
                                  </span>
                                )}
                                {!isTeamMember && conv.unread && (
                                  <span
                                    className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      fontSize: "0.7rem",
                                      fontWeight: "600"
                                    }}
                                  >
                                    •
                                  </span>
                                )}
                              </div>

                              <p
                                className="mb-2 text-muted text-truncate"
                                style={{
                                  fontSize: "0.8rem",
                                  lineHeight: "1.4"
                                }}
                              >
                                {conv.lastMessage}
                              </p>

                              <div className="d-flex align-items-center">
                                {isPending ? (
                                  <small className="text-muted" style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                                    ● Pending activation
                                  </small>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faClock} className="me-1 text-muted" size="xs" />
                                    <small className="text-muted" style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                                      {formatTime(conv.timestamp)}
                                    </small>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            {/* Main Chat Area */}
            <Col xl={9}>
              <Card className="h-100 border-0 rounded-0 shadow-sm">
                <Card.Header 
                  className="border-bottom py-4"
                  style={{ backgroundColor: "#ffffff" }}
                >
                  <div className="d-flex align-items-center">
                    {activeConversation ? (
                      <>
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm"
                          style={{
                            width: "48px",
                            height: "48px",
                            background: getConversationStyling(activeConversation.agentType).gradient,
                            border: "3px solid rgba(102, 126, 234, 0.1)"
                          }}
                        >
                          <FontAwesomeIcon icon={getConversationStyling(activeConversation.agentType).icon} className="text-white" style={{ fontSize: "1.1rem" }} />
                        </div>
                        <div>
                          <h5 className="mb-1 text-body-emphasis fw-bold" style={{ fontSize: "1.1rem" }}>
                            {activeConversation.name}
                          </h5>
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon 
                              icon={faCircle} 
                              className="text-success me-2" 
                              style={{ fontSize: "0.5rem" }}
                            />
                            <small className="text-success fw-semibold" style={{ fontSize: "0.8rem" }}>
                              {activeConversation.agentType === 'human-collaborator' ? 'Team Member Chat' : 'AI Assistant Active'}
                            </small>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm"
                          style={{
                            width: "48px",
                            height: "48px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            border: "3px solid rgba(102, 126, 234, 0.1)"
                          }}
                        >
                          <FontAwesomeIcon icon={faBolt} className="text-white" style={{ fontSize: "1.1rem" }} />
                        </div>
                        <div>
                          <h5 className="mb-1 text-body-emphasis fw-bold" style={{ fontSize: "1.1rem" }}>
                            Select a Conversation
                          </h5>
                          <div className="d-flex align-items-center">
                            <small className="text-muted fw-semibold" style={{ fontSize: "0.8rem" }}>
                              Choose an AI agent or team member to start chatting
                            </small>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Card.Header>
                
                <Card.Body className="d-flex flex-column p-0" style={{ backgroundColor: "#fafbfc" }}>
                  {/* Messages Container */}
                  <div 
                    ref={chatContainerRef}
                    className="flex-grow-1 p-4"
                    style={{ 
                      height: "calc(100vh - 300px)", 
                      overflowY: "auto",
                      background: "linear-gradient(180deg, #fafbfc 0%, #f8f9fa 100%)"
                    }}
                  >
                    {loadingChatHistory ? (
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="text-center">
                          <div 
                            className="spinner-border text-primary mb-4"
                            style={{ width: "3rem", height: "3rem" }}
                          />
                          <p className="text-muted mb-0 fw-semibold">Loading conversation...</p>
                          <small className="text-muted">Please wait while we fetch your chat history</small>
                        </div>
                      </div>
                    ) : showInlineSelection ? (
                      <div className="d-flex flex-column" style={{ maxHeight: "45vh" }}>
                        {/* Inline Selection Header with Search */}
                        <div className="border-bottom bg-white p-3" style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <h6 className="mb-0 text-body-emphasis">To: Start a new conversation</h6>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-muted p-0"
                              onClick={() => setShowInlineSelection(false)}
                              style={{ fontSize: "1.2rem", lineHeight: 1 }}
                              title="Close"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                          <InputGroup size="sm">
                            <InputGroup.Text className="bg-white border-end-0">
                              <FontAwesomeIcon icon={faSearch} className="text-muted" size="sm" />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Search AI agents or team members..."
                              value={inlineSearchQuery}
                              onChange={(e) => setInlineSearchQuery(e.target.value)}
                              className="border-start-0"
                              autoFocus
                              style={{
                                boxShadow: "none",
                                fontSize: "0.9rem"
                              }}
                            />
                          </InputGroup>
                        </div>

                        {/* Inline Selection List */}
                        <div className="overflow-auto bg-white" style={{ maxHeight: "calc(45vh - 100px)" }}>
                          {/* AI Assistants Section */}
                          <div className="p-3">
                            <div className="d-flex align-items-center mb-3">
                              <FontAwesomeIcon icon={faRobot} className="text-primary me-2" />
                              <h6 className="mb-0 text-muted fw-semibold">AI Assistants</h6>
                            </div>
                            {aiAgents
                              .filter(agent =>
                                agent.name.toLowerCase().includes(inlineSearchQuery.toLowerCase()) ||
                                agent.description.toLowerCase().includes(inlineSearchQuery.toLowerCase())
                              )
                              .map((agent) => (
                                <div
                                  key={agent.id}
                                  className="d-flex align-items-center p-3 mb-2 rounded cursor-pointer hover-bg-light"
                                  onClick={() => {
                                    handleSelectAgent(agent);
                                    setShowInlineSelection(false);
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    transition: "all 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "rgba(13, 110, 253, 0.08)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                  }}
                                >
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      background: agent.gradient
                                    }}
                                  >
                                    <FontAwesomeIcon icon={agent.icon} className="text-white" size="sm" />
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="fw-semibold mb-1">{agent.name}</div>
                                    <small className="text-muted">{agent.description}</small>
                                  </div>
                                </div>
                              ))}
                          </div>

                          {/* Team Members Section */}
                          <div className="p-3 border-top">
                            <div className="d-flex align-items-center mb-3">
                              <FontAwesomeIcon icon={faUsers} className="text-warning me-2" />
                              <h6 className="mb-0 text-muted fw-semibold">Team Members</h6>
                            </div>
                            {collaborators
                              .filter(member => {
                                const fullName = `${member.invitee.collaborator_first_name} ${member.invitee.collaborator_last_name}`;
                                return fullName.toLowerCase().includes(inlineSearchQuery.toLowerCase()) ||
                                  member.invitee.collaborator_email.toLowerCase().includes(inlineSearchQuery.toLowerCase());
                              })
                              .map((member) => {
                                const fullName = `${member.invitee.collaborator_first_name} ${member.invitee.collaborator_last_name}`;
                                const isPending = member.status === 'pending' || !member.isAuthenticated;

                                return (
                                  <div
                                    key={member.collaborator_id}
                                    className="d-flex align-items-center p-3 mb-2 rounded cursor-pointer"
                                    onClick={() => {
                                      if (!isPending) {
                                        handleSelectCollaborator(member);
                                        setShowInlineSelection(false);
                                      }
                                    }}
                                    style={{
                                      cursor: isPending ? 'not-allowed' : 'pointer',
                                      opacity: isPending ? 0.6 : 1,
                                      transition: "all 0.2s ease"
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isPending) {
                                        e.currentTarget.style.backgroundColor = "rgba(13, 110, 253, 0.08)";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = "transparent";
                                    }}
                                  >
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        background: isPending
                                          ? "linear-gradient(135deg, #6c757d, #adb5bd)"
                                          : "linear-gradient(135deg, #ffc107, #fd7e14)"
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faUser} className="text-white" size="sm" />
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="fw-semibold mb-1">
                                        {fullName}
                                        {isPending && (
                                          <span className="badge bg-secondary ms-2" style={{ fontSize: '0.65rem' }}>
                                            Pending
                                          </span>
                                        )}
                                      </div>
                                      <small className="text-muted">{member.invitee.collaborator_email}</small>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    ) : !activeConversation ? (
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="text-center">
                          <FontAwesomeIcon icon={faMessage} className="text-muted mb-4" size="3x" />
                          <h4 className="text-muted mb-3">Start a Conversation</h4>
                          <p className="text-muted mb-4">Choose an AI assistant or chat with a team member</p>
                          <div className="d-flex gap-3 justify-content-center">
                            <Button variant="primary" onClick={startNewConversation}>
                              <FontAwesomeIcon icon={faPlus} className="me-2" />
                              New Conversation
                            </Button>
                            <Button
                              variant="warning"
                              onClick={() => navigate('/agent-portal/ask-the-broker')}
                              style={{ fontWeight: 600 }}
                            >
                              <FontAwesomeIcon icon={faUsers} className="me-2" />
                              Ask the Broker
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      renderedMessages
                    )}
                    
                    {loading && (
                      <div className="d-flex justify-content-start mb-4">
                        <div className="d-flex align-items-start" style={{ maxWidth: "85%" }}>
                          <div 
                            className="bg-white p-3 border shadow-sm" 
                            style={{ 
                              borderRadius: "18px 18px 18px 6px",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)"
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div className="spinner-border spinner-border-sm text-primary me-3" />
                              <span className="text-muted fw-semibold">
                                {activeConversation?.agentType === 'human-collaborator' ? 'Sending message...' : 'AI is thinking...'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Input Area */}
                  <div
                    className="border-top p-4"
                    style={{
                      backgroundColor: "#ffffff",
                      boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.04)"
                    }}
                  >
                    <div className="position-relative">
                      {/* File Attachment Previews */}
                      {(fileAttachments.length > 0 || imageAttachments.length > 0) && (
                        <div className="mb-3 p-3 bg-light rounded">
                          {imageAttachments.length > 0 && (
                            <div className="mb-2">
                              <small className="text-muted d-block mb-2">Images ({imageAttachments.length})</small>
                              <div className="d-flex gap-2 flex-wrap">
                                {imageAttachments.map((file, index) => (
                                  <div key={index} className="position-relative">
                                    <img
                                      src={URL.createObjectURL(file)}
                                      alt={file.name}
                                      style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "8px" }}
                                    />
                                    <Button
                                      size="sm"
                                      variant="danger"
                                      className="position-absolute top-0 end-0 rounded-circle p-0"
                                      style={{ width: "20px", height: "20px", fontSize: "0.7rem" }}
                                      onClick={() => setImageAttachments(imageAttachments.filter((_, i) => i !== index))}
                                    >
                                      ×
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {fileAttachments.length > 0 && (
                            <div>
                              <small className="text-muted d-block mb-2">Files ({fileAttachments.length})</small>
                              {fileAttachments.map((file, index) => (
                                <div key={index} className="d-flex align-items-center justify-content-between p-2 bg-white rounded mb-2">
                                  <div className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faPaperclip} className="text-primary" />
                                    <small>{file.name}</small>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="text-danger p-0"
                                    onClick={() => setFileAttachments(fileAttachments.filter((_, i) => i !== index))}
                                  >
                                    ×
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="position-relative">
                        <Form.Control
                          as="textarea"
                          rows={1}
                          placeholder={activeConversation
                            ? activeConversation.agentType === 'human-collaborator'
                              ? `Message ${activeConversation.participantName}...`
                              : "Ask me anything about your leads, market insights, or CRM optimization..."
                            : "Select a conversation to start messaging..."
                          }
                          value={inputMessage}
                          onChange={(e) => {
                            setInputMessage(e.target.value);
                            // Auto-resize textarea
                            e.target.style.height = "auto";
                            e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
                          }}
                          onKeyPress={(e) => {
                            if (e.key === "Enter" && !e.shiftKey && !loading && inputMessage.trim() && activeConversation) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          className="border-0 shadow-sm"
                          style={{
                            minHeight: "56px",
                            borderRadius: "28px",
                            paddingLeft: "60px",
                            paddingRight: "80px",
                            paddingTop: "16px",
                            paddingBottom: "16px",
                            fontSize: "0.95rem",
                            backgroundColor: "#f8f9fa",
                            boxShadow: "inset 0 2px 8px rgba(0, 0, 0, 0.06)",
                            resize: "none"
                          }}
                          disabled={loading || !activeConversation}
                        />

                        {/* File Upload Buttons - Positioned on the left */}
                        <div className="position-absolute start-0 top-50 translate-middle-y d-flex gap-2 ms-2" style={{ zIndex: 10 }}>
                          {/* Image Upload Button */}
                          <label htmlFor="image-upload" className="btn btn-link p-0 text-primary" style={{ cursor: "pointer" }}>
                            <FontAwesomeIcon icon={faImage} style={{ fontSize: "1.2rem" }} />
                          </label>
                          <Form.Control
                            id="image-upload"
                            type="file"
                            accept="image/*"
                            multiple
                            className="d-none"
                            onChange={(e: any) => {
                              if (e.target.files) {
                                setImageAttachments([...imageAttachments, ...Array.from(e.target.files) as File[]]);
                              }
                            }}
                          />

                          {/* File Upload Button */}
                          <label htmlFor="file-upload" className="btn btn-link p-0 text-primary" style={{ cursor: "pointer" }}>
                            <FontAwesomeIcon icon={faPaperclip} style={{ fontSize: "1.2rem" }} />
                          </label>
                          <Form.Control
                            id="file-upload"
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
                            multiple
                            className="d-none"
                            onChange={(e: any) => {
                              if (e.target.files) {
                                setFileAttachments([...fileAttachments, ...Array.from(e.target.files) as File[]]);
                              }
                            }}
                          />
                        </div>

                        <Button
                          variant="primary"
                          onClick={handleSendMessage}
                          disabled={loading || (!inputMessage.trim() && fileAttachments.length === 0 && imageAttachments.length === 0) || !activeConversation}
                          className="rounded-circle position-absolute end-0 top-50 translate-middle-y me-2 shadow-sm d-flex align-items-center justify-content-center"
                          style={{
                            width: "48px",
                            height: "48px",
                            zIndex: 10,
                            background: (inputMessage.trim() || fileAttachments.length > 0 || imageAttachments.length > 0) && !loading && activeConversation
                              ? "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)"
                              : undefined,
                            border: "none",
                            boxShadow: "0 4px 12px rgba(13, 110, 253, 0.3)"
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-center" style={{ width: "100%", height: "100%" }}>
                            {loading ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: "1rem" }} />
                            )}
                          </div>
                        </Button>
                      </div>

                      {error && (
                        <div className="text-danger mt-3 d-flex align-items-center" style={{ fontSize: "0.875rem" }}>
                          <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                          {formatErrorForReact(error)}
                        </div>
                      )}
                    </div>
                    
                    <div className="d-flex align-items-center justify-content-center mt-3">
                      <small 
                        className="text-muted fw-medium"
                        style={{ fontSize: "0.8rem", letterSpacing: "0.3px" }}
                      >
                        Powered by KAIROS IQ AI • Your data is secure and private
                      </small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <AgentSelectionModal />
          <AddTeamMemberModal 
            show={showAddTeamMemberModal}
            onHide={() => setShowAddTeamMemberModal(false)}
            onSuccess={() => {
              fetchTeamMembers(); // Refresh team members list
              toast.success('Team member added successfully!');
            }}
            brokerageInfo={brokerageInfo || undefined}
          />
        </div>
      )}
      {!isAuthorized && <Backdrop />}
    </>
  );
};

export default Chat;
